import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bug, Search, ChevronDown, ChevronUp, Image, FileText, X, Edit3, Save, RotateCcw, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { instancesApi } from '../api';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Badge, Spinner, PageHeader } from '../components/UI';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
});

function ChunkCard({ chunk, instanceId, onImageClick, onUpdated }) {
  const { t: tc } = useTranslation('common');
  const { t } = useTranslation('debug');
  const [expanded, setExpanded]       = useState(false);
  const [editing, setEditing]         = useState(false);
  const [editText, setEditText]       = useState('');
  const [editVision, setEditVision]   = useState('');
  const [saving, setSaving]           = useState(false);

  const startEdit = (e) => {
    e.stopPropagation();
    setEditText(chunk.displayText || chunk.text || '');
    setEditVision(chunk.visionDescription || '');
    setEditing(true);
    if (!expanded) setExpanded(e => !e);
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditing(false);
  };

  const saveEdit = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      // Send explicit null/string — never undefined — so backend knows "" is intentional
      await API.put(`/debug/${instanceId}/chunk/${chunk.id}`, {
        text: editText,
        visionDescription: editVision === '' ? null : editVision
      });
      toast.success(t('chunkUpdated'));
      setEditing(false);
      onUpdated();
    } catch (err) {
      toast.error(t('savingError', { message: err.response?.data?.error || err.message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: 'var(--bg-2)',
      border: `1px solid ${chunk.manuallyEdited ? 'rgba(245,158,11,0.4)' : 'var(--border)'}`,
      borderRadius: 8, overflow: 'hidden', marginBottom: 8
    }}>
      {/* Header */}
      <div
        onClick={() => !editing && setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', cursor: editing ? 'default' : 'pointer',
          borderBottom: expanded ? '1px solid var(--border)' : 'none'
        }}
      >
        <FileText size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-3)' }}>
              {String(chunk.id).substring(0, 8)}...
            </span>
            {chunk.source && <Badge>{chunk.source}</Badge>}
            {chunk.pageNum && <Badge color="cyan">p.{chunk.pageNum}</Badge>}
            {chunk.ocr && <Badge color="yellow">OCR</Badge>}
            {chunk.isVisualPage && <Badge color="purple">🤖 Vision</Badge>}
            {chunk.imageUrl && <Badge color="blue">🖼 img</Badge>}
            {chunk.manuallyEdited && <Badge color="orange">✏️ {t('edited')}</Badge>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
            {chunk.textLength} chars
          </span>
          {chunk.imageUrl && (
            <button
              onClick={e => { e.stopPropagation(); onImageClick(chunk); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                background: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.4)',
                borderRadius: 4, color: 'var(--accent2)', fontSize: 10, cursor: 'pointer'
              }}
            >
              <Image size={10} /> {tc('open')}
            </button>
          )}
          {/* Edit button */}
          {!editing ? (
            <button
              onClick={startEdit}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.4)',
                borderRadius: 4, color: 'var(--accent)', fontSize: 10, cursor: 'pointer'
              }}
            >
              <Edit3 size={10} /> {tc('edit')}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={saveEdit}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                  background: 'rgba(22,163,74,0.2)', border: '1px solid rgba(22,163,74,0.5)',
                  borderRadius: 4, color: '#16a34a', fontSize: 10, cursor: 'pointer'
                }}
              >
                {saving ? <Spinner size={10} /> : <Save size={10} />} {tc('save')}
              </button>
              <button
                onClick={cancelEdit}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 4, color: '#ef4444', fontSize: 10, cursor: 'pointer'
                }}
              >
                <RotateCcw size={10} /> {tc('cancel')}
              </button>
            </div>
          )}
          {expanded
            ? <ChevronUp size={13} style={{ color: 'var(--text-3)' }} />
            : <ChevronDown size={13} style={{ color: 'var(--text-3)' }} />
          }
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '12px 14px' }}>
          {chunk.imageUrl && (
            <div
              onClick={() => onImageClick(chunk)}
              style={{
                float: 'right', marginLeft: 16, marginBottom: 8,
                width: 160, cursor: 'pointer', borderRadius: 6, overflow: 'hidden',
                border: '1px solid var(--border)'
              }}
            >
              <img src={chunk.imageUrl} alt="" style={{ width: '100%', display: 'block' }} />
              <div style={{ padding: '4px 6px', background: 'var(--bg-3)', fontSize: 10, color: 'var(--accent2)', textAlign: 'center' }}>
                {t('clickToEnlarge')}
              </div>
            </div>
          )}

          {/* Vision description */}
          {editing ? (
            <>
              {/* Vision edit */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700, letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                  {t('visionLabel')}
                </label>
                <textarea
                  value={editVision}
                  onChange={e => setEditVision(e.target.value)}
                  rows={5}
                  style={{
                    width: '100%', background: 'rgba(139,92,246,0.08)',
                    border: '1px solid rgba(139,92,246,0.4)', borderRadius: 6,
                    color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12,
                    padding: '10px', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box'
                  }}
                  placeholder={t('visionPlaceholder')}
                />
              </div>
              {/* Text edit */}
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 700, letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                  {chunk.ocr ? t('chunkTextOcr', { suffix: t('ocrRaw') }) : t('chunkTextLabel')}
                </label>
                <textarea
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  rows={8}
                  style={{
                    width: '100%', background: 'var(--bg-3)',
                    border: '1px solid var(--accent)', borderRadius: 6,
                    color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12,
                    padding: '10px', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>
                {t('reEmbedNote')}
              </div>
            </>
          ) : (
            <>
              {chunk.visionDescription && (
                <div style={{
                  marginBottom: 12, padding: '10px 12px',
                  background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)',
                  borderRadius: 6
                }}>
                  <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>
                    {t('visionLabelShort')}
                  </div>
                  <pre style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                    {chunk.visionDescription}
                  </pre>
                </div>
              )}
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 6, letterSpacing: 1, fontWeight: 700 }}>
                {chunk.isVisualPage ? t('ocrTextLabel') : t('chunkTextLabel')}
                {chunk.manuallyEdited && <span style={{ color: '#f59e0b', marginLeft: 8 }}>{t('editedManually')}</span>}
              </div>
              <pre style={{
                fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-2)',
                lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0
              }}>
                {chunk.displayText || chunk.text || t('emptyText')}
              </pre>
            </>
          )}
          <div style={{ clear: 'both' }} />
        </div>
      )}
    </div>
  );
}

export default function Debug() {
  const { t } = useTranslation('debug');
  const qc = useQueryClient();
  const [selectedInstanceId, setSelectedInstanceId] = useState('');
  const [searchDoc, setSearchDoc]   = useState('');
  const [searchText, setSearchText] = useState('');
  const [offset, setOffset]         = useState(null);
  const [lightbox, setLightbox]     = useState(null);
  const [limit] = useState(20);

  const { data: instancesData } = useQuery({ queryKey: ['instances'], queryFn: instancesApi.list });
  const instances = instancesData?.instances || [];

  const chunksKey = ['debug-chunks', selectedInstanceId, searchDoc, offset];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: chunksKey,
    queryFn: () => API.get(`/debug/${selectedInstanceId}/chunks`, {
      params: { limit, offset, ...(searchDoc ? { docId: searchDoc } : {}) }
    }).then(r => r.data),
    enabled: !!selectedInstanceId
  });

  const instance = instances.find(i => i.id === selectedInstanceId);

  const filteredChunks = (data?.chunks || []).filter(chunk => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return (
      (chunk.text || '').toLowerCase().includes(q) ||
      (chunk.displayText || '').toLowerCase().includes(q) ||
      (chunk.visionDescription || '').toLowerCase().includes(q) ||
      (chunk.source || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="page-content" style={{ maxWidth: 1100 }}>
      <PageHeader
        title="Debug Chunks"
        subtitle={t('subtitle')}
        icon={<Bug size={20} style={{ color: 'var(--accent)' }} />}
      />

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select
          value={selectedInstanceId}
          onChange={e => { setSelectedInstanceId(e.target.value); setOffset(null); }}
          style={{
            background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6,
            color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12,
            padding: '8px 12px', outline: 'none', minWidth: 220
          }}
        >
          <option value="">{t('selectInstance')}</option>
          {instances.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>

        {instance && (
          <select
            value={searchDoc}
            onChange={e => { setSearchDoc(e.target.value); setOffset(null); }}
            style={{
              background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12,
              padding: '8px 12px', outline: 'none', minWidth: 220
            }}
          >
            <option value="">{t('allDocuments')}</option>
            {instance.documents?.filter(d => d.status === 'ready').map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}

        {instance && (
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <Search size={13} style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-3)', pointerEvents: 'none'
            }} />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{
                width: '100%', background: 'var(--bg-2)', border: '1px solid var(--border)',
                borderRadius: 6, color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
                fontSize: 12, padding: '8px 12px 8px 32px', outline: 'none', boxSizing: 'border-box'
              }}
            />
            {searchText && (
              <button onClick={() => setSearchText('')} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2
              }}>
                <X size={12} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Stats bar */}
      {instance && (
        <div style={{
          display: 'flex', gap: 16, padding: '10px 14px', marginBottom: 16,
          background: 'var(--bg-2)', borderRadius: 8, border: '1px solid var(--border)',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{instance.vectorCount || 0}</span> {t('totalVectors')}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            <span style={{ color: 'var(--accent2)', fontWeight: 700 }}>{instance.documents?.filter(d => d.status === 'ready').length || 0}</span> {t('documentsReady')}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
            {t('collection')}: <span style={{ color: 'var(--text-2)' }}>{instance.collectionName}</span>
          </span>
        </div>
      )}

      {/* Content */}
      {!selectedInstanceId ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
          <Bug size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div>{t('selectPrompt')}</div>
        </div>
      ) : isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spinner size={32} />
        </div>
      ) : error ? (
        <div style={{ padding: 20, color: 'var(--red)', background: 'var(--bg-2)', borderRadius: 8 }}>
          {t('error', { message: error.message })}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-3)' }}>
            {t('showing')} <strong style={{ color: 'var(--text-1)' }}>{filteredChunks.length}</strong>
            {searchText && <> {t('of')} {data?.chunks?.length || 0}</>} {t('chunks')}
            {searchDoc && ` ${t('selectedDocument')}`}
            {searchText && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>{t('filter')} "{searchText}"</span>}
          </div>

          {filteredChunks.length === 0 && searchText && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
              <Search size={28} style={{ marginBottom: 8, opacity: 0.3 }} />
              <div>{t('noChunks', { text: searchText })}</div>
            </div>
          )}

          {filteredChunks.map(chunk => (
            <ChunkCard
              key={chunk.id}
              chunk={chunk}
              instanceId={selectedInstanceId}
              onImageClick={c => setLightbox(c)}
              onUpdated={refetch}
            />
          ))}

          {/* Pagination */}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            {offset && (
              <button
                onClick={() => setOffset(null)}
                style={{
                  padding: '8px 16px', background: 'var(--bg-2)', border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--text-2)', cursor: 'pointer', fontSize: 12
                }}
              >
                {t('toStart')}
              </button>
            )}
            {data?.nextOffset && (
              <button
                onClick={() => setOffset(data.nextOffset)}
                style={{
                  padding: '8px 16px', background: 'var(--accent)', border: 'none',
                  borderRadius: 6, color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600
                }}
              >
                {t('nextPage', { limit })}
              </button>
            )}
          </div>
        </>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 8, padding: '6px 10px', background: 'var(--bg-2)',
              borderRadius: '6px 6px 0 0', border: '1px solid var(--border)'
            }}>
              <span style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                {lightbox.source} — {t('page')} {lightbox.pageNum}
              </span>
              <button onClick={() => setLightbox(null)}
                style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>
            <img
              src={lightbox.imageUrl} alt=""
              style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', display: 'block', border: '1px solid var(--border)', borderRadius: '0 0 6px 6px' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
